import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, CheckCircle2, AlertTriangle, Loader2, Sparkles } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Button,
  Input,
  Label,
  Badge,
  Alert,
  AlertDescription,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent
} from '../ui';
import {
  getAllGroups,
  getGroupPlayers,
  getTorneoCategoriasConvocadas,
  getCategoriasDeGrupo,
  esTorneoPropio,
  crearGrupoTorneo,
  inscribirGrupo,
  getMaxJugadoresGrupo,
  jugadoresElegibles,
  getStaffGroups
} from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { useOrganization } from '../../contexts/OrganizationContext';
import { esLocalParaInscribir } from '../../lib/torneoOrigen';
import { gruposParaTorneoInterno, deporteDelTorneo } from '../../lib/gruposParaTorneo';
import { usePermissions } from '../../hooks/usePermissions';
import { puedeCapturarJuegos } from '../../lib/permisosJuegos';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import { cn, fixMojibake } from '../../lib/utils';
import { etiquetaDivision, grupoEntraADivision, idDivision, nombreConRama, normalizarRama, separarGruposPorDivision } from '../../lib/division';
import { useUnsavedGuard } from '../../hooks/useUnsavedGuard';
import { mensajeErrorInscripcion } from '../../lib/torneoErrores';
import NumerosGrupoEditor from '../groups/NumerosGrupoEditor';

// Inscribir un grupo, por los dos caminos que existen.
//
//   LOCAL   (el torneo es de tu club): primero el grupo, y la categoria SALE de el.
//           Queda inscrito de una vez -- no hay a quien pedirle permiso.
//   EXTERNO (te invitaron): primero la categoria convocada, luego el grupo. Queda
//           como solicitud y la organizadora resuelve.
//
// Los dos escriben en torneos_inscripciones; quien bifurca es el RPC inscribir_grupo.
// El orden se invierte a proposito: en local el grupo es el contenedor que manda, y
// preguntar por la categoria antes de saber el grupo seria preguntar al aire.

const formatRangoEdad = (cat) => {
  if (!cat) return '';
  const anioInicio = cat.anio_inicio;
  const anioFin = cat.anio_fin;
  if (typeof anioInicio === 'number' && typeof anioFin === 'number') {
    return anioInicio === anioFin ? `Nacidos en ${anioFin}` : `Nacidos ${anioFin}-${anioInicio}`;
  }
  if (typeof cat.edad_min === 'number' && typeof cat.edad_max === 'number') {
    return `${cat.edad_min}-${cat.edad_max} años`;
  }
  if (typeof cat.edad_min === 'number') return `Desde ${cat.edad_min} años`;
  if (typeof cat.edad_max === 'number') return `Hasta ${cat.edad_max} años`;
  return 'Sin límite de edad';
};

// El nombre se limpia ANTES de componer: fixMojibake sobre "Niños U14 · Femenil" ya
// armado dañaria el punto medio del separador.
const etiquetaLimpia = (c) => etiquetaDivision({ ...c, nombre: fixMojibake(c?.nombre) });

const InscribirGrupoModal = ({ torneo, gruposYaInscritos = [], onClose, onSuccess, grupoFijo = null, grupoFijoNombre = '' }) => {
  const guard = useUnsavedGuard({ onClose });
  const { staff } = useAuth();
  const { role, isPlatformAdmin } = usePermissions();
  // Los grupos a los que estoy asignado. Sin esto el modal ofrecia TODOS los del club:
  // el boton se abre con canManageTorneos (admin o head_coach), y un head_coach sin
  // asignacion veia grupos que la base -- desde 20260905 -- rechaza inscribir.
  const [misGrupos, setMisGrupos] = useState([]);
  const { alert, ConfirmDialog } = useConfirmDialog();

  // null mientras no se sabe: la pantalla no debe adivinar el flujo. Decide la
  // organizadora del torneo (esLocalParaInscribir); la RPC es_torneo_propio queda como
  // ultimo recurso, porque contesta segun el club del USUARIO y viendo como otra org
  // abria el flujo de la red en un torneo interno.
  const { org } = useOrganization();
  const [esLocal, setEsLocal] = useState(() => esLocalParaInscribir(torneo, org?.id));
  const [cargando, setCargando] = useState(true);
  const [errMsg, setErrMsg] = useState(null);

  // Externo: las categorias que convoco la organizadora.
  const [convocadas, setConvocadas] = useState([]);
  // Local: las categorias del grupo elegido (null = todavia no se consultan).
  const [catsDelGrupo, setCatsDelGrupo] = useState(null);

  const [categoriaId, setCategoriaId] = useState('');
  const [modoGrupo, setModoGrupo] = useState('existente');

  const [grupos, setGrupos] = useState([]);
  const [search, setSearch] = useState('');
  const [grupoId, setGrupoId] = useState(grupoFijo || '');

  const [jugadores, setJugadores] = useState([]);
  const [loadingJugadores, setLoadingJugadores] = useState(false);

  const [nombreNuevoGrupo, setNombreNuevoGrupo] = useState('');
  const [grupoCreado, setGrupoCreado] = useState(null); // {id, nombre}
  const [creandoGrupo, setCreandoGrupo] = useState(false);

  const [maxJugadores, setMaxJugadores] = useState(null);

  const [enviando, setEnviando] = useState(false);
  const [numEstado, setNumEstado] = useState(null);
  const guardarNumerosRef = useRef(null);

  const idsYaInscritos = useMemo(
    () => new Set((gruposYaInscritos || []).map((g) => g.grupo_id || g.id)),
    [gruposYaInscritos]
  );

  // Carga inicial: quien organiza, y de ahi que se necesita.
  useEffect(() => {
    let mounted = true;
    (async () => {
      setCargando(true);
      try {
        let local = esLocal;
        if (local === null) {
          const { data } = await esTorneoPropio(torneo.id);
          local = data;
          if (!mounted) return;
          setEsLocal(local);
        }

        const [gruposRes, catRes, misRes] = await Promise.all([
          getAllGroups(),
          local ? Promise.resolve(null) : getTorneoCategoriasConvocadas(torneo.id),
          // Lleva staffId obligatorio: sin el devuelve vacio y esconderia TODOS los
          // grupos a quien no sea admin.
          staff?.id ? getStaffGroups(staff.id).catch(() => null) : Promise.resolve(null)
        ]);
        if (!mounted) return;

        setGrupos(gruposRes?.data || []);
        setMisGrupos(misRes?.data || []);
        if (catRes) {
          if (catRes.error) throw catRes.error;
          setConvocadas(catRes.data || []);
        }
      } catch (err) {
        console.error(err);
        setErrMsg(err?.message || 'No pudimos cargar los datos del torneo.');
      } finally {
        if (mounted) setCargando(false);
      }
    })();
    return () => { mounted = false; };
  }, [torneo.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Espera a saber de quien es el torneo: en el interno no hay regla de organizador
  // (solo el tope del plan, si aplica).
  useEffect(() => {
    if (esLocal === null) return undefined;
    let mounted = true;
    (async () => {
      const { data } = await getMaxJugadoresGrupo(torneo.id, staff?.org_id, { interno: esLocal });
      if (mounted) setMaxJugadores(data);
    })();
    return () => { mounted = false; };
  }, [torneo.id, staff?.org_id, esLocal]);

  // En local, la categoria sale del grupo: se consulta al elegirlo.
  useEffect(() => {
    if (!esLocal || !grupoId) {
      setCatsDelGrupo(null);
      return;
    }
    let mounted = true;
    (async () => {
      const { data } = await getCategoriasDeGrupo(grupoId);
      if (!mounted) return;
      const cats = data || [];
      setCatsDelGrupo(cats);
      // Una sola: se hereda sin preguntar. Varias: elige el club, entre las del grupo.
      setCategoriaId(cats.length === 1 ? cats[0].id : '');
    })();
    return () => { mounted = false; };
  }, [esLocal, grupoId]);

  // Los jugadores del grupo, para medir elegibilidad. Se cargan una vez por grupo y
  // la evaluacion se recalcula sola cuando cambia la categoria.
  useEffect(() => {
    if (!grupoId) {
      setJugadores([]);
      return;
    }
    let mounted = true;
    (async () => {
      setLoadingJugadores(true);
      try {
        const { data, error } = await getGroupPlayers(grupoId);
        if (error) throw error;
        if (mounted) setJugadores((data || []).map((row) => row.jugadores).filter(Boolean));
      } catch (err) {
        console.error(err);
        if (mounted) setJugadores([]);
      } finally {
        if (mounted) setLoadingJugadores(false);
      }
    })();
    return () => { mounted = false; };
  }, [grupoId]);

  // De donde salen las categorias: del grupo (local) o de la convocatoria (externo).
  const opcionesCategoria = useMemo(
    () => (esLocal ? (catsDelGrupo || []) : convocadas),
    [esLocal, catsDelGrupo, convocadas]
  );

  // En la red la opcion es una DIVISION (categoria + genero, 20261005): su llave es
  // convocatoria_id, porque una categoria puede venir varias veces (U14 Femenil, U14
  // Varonil). En interno es la categoria del grupo, como antes.
  const categoriaSeleccionada = useMemo(
    () => opcionesCategoria.find((c) => (esLocal ? (c.categoria_id || c.id) : idDivision(c)) === categoriaId) || null,
    [opcionesCategoria, categoriaId, esLocal]
  );

  const elegibilidad = useMemo(() => {
    if (!grupoId || !categoriaSeleccionada || loadingJugadores) return null;
    const evaluados = jugadoresElegibles(categoriaSeleccionada, jugadores);
    return {
      total: evaluados.length,
      elegibles: evaluados.filter((j) => j.elegible).length,
      jugadores: evaluados
    };
  }, [grupoId, categoriaSeleccionada, jugadores, loadingJugadores]);

  const gruposDisponibles = useMemo(() => {
    const term = search.trim().toLowerCase();
    // En interno, solo los grupos del deporte del torneo (el de su temporada). En la
    // red acota la categoria convocada, no esto.
    const base = esLocal ? gruposParaTorneoInterno(grupos, deporteDelTorneo(torneo)) : grupos;
    return base
      .filter((g) => !idsYaInscritos.has(g.id))
      // Solo los que de verdad puedo inscribir. Mismo criterio que la base
      // (`puede_capturar_juegos`), para no ofrecer lo que va a rechazar.
      .filter((g) => puedeCapturarJuegos({
        rol: role,
        esPlatformAdmin: isPlatformAdmin,
        asignaciones: misGrupos,
        grupoId: g.id,
      }))
      .filter((g) => (term ? fixMojibake(g.nombre_del_grupo || '').toLowerCase().includes(term) : true));
  }, [grupos, idsYaInscritos, search, role, isPlatformAdmin, misGrupos]);

  // Red (20261005): el grupo entra solo a la division de su mismo genero (1 a 1; Mixto
  // solo con Mixto). Los que no tienen genero se avisan: hay que asignarselo en el grupo.
  const porGenero = useMemo(
    () => (esLocal || !categoriaSeleccionada
      ? null
      : separarGruposPorDivision(gruposDisponibles, categoriaSeleccionada.rama)),
    [esLocal, categoriaSeleccionada, gruposDisponibles]
  );
  const gruposOfrecidos = porGenero ? porGenero.compatibles : gruposDisponibles;

  // Desde la ficha de un grupo (grupoFijo) en la red: solo se ofrecen las divisiones de
  // su mismo genero, para no llegar al RPC con GENERO_CRUZADO / GRUPO_SIN_GENERO.
  const gruposFijoEnRed = !esLocal && Boolean(grupoFijo);
  const ramaGrupoFijo = gruposFijoEnRed ? grupos.find((g) => g.id === grupoFijo)?.rama : null;
  const ramaFijaNormalizada = normalizarRama(ramaGrupoFijo);
  const grupoFijoSinGenero = gruposFijoEnRed && !ramaFijaNormalizada;
  const divisionesOfrecidas = useMemo(
    () => (gruposFijoEnRed ? convocadas.filter((c) => grupoEntraADivision(ramaGrupoFijo, c.rama)) : convocadas),
    [gruposFijoEnRed, convocadas, ramaGrupoFijo]
  );

  const handleCategoriaChange = (value) => {
    guard.markDirty();
    setCategoriaId(value);
    setErrMsg(null);
    // En externo la categoria manda: cambiarla reinicia la eleccion de grupo.
    if (!esLocal && !grupoFijo) {
      setGrupoId('');
      setGrupoCreado(null);
      setNombreNuevoGrupo('');
    }
  };

  const handleSelectGrupo = (id) => {
    guard.markDirty();
    setGrupoId(id);
    setErrMsg(null);
  };

  const handleModoChange = (value) => {
    setModoGrupo(value);
    setGrupoId('');
    setGrupoCreado(null);
    setNombreNuevoGrupo('');
    setErrMsg(null);
  };

  const handleCrearGrupo = async () => {
    const nombre = nombreNuevoGrupo.trim();
    if (!nombre || !categoriaId) return;
    setCreandoGrupo(true);
    setErrMsg(null);
    try {
      const { data: nuevoId, error } = await crearGrupoTorneo({
        torneoId: torneo.id,
        categoriaId: categoriaSeleccionada?.categoria_id || null,
        convocatoriaId: categoriaSeleccionada?.convocatoria_id || null,
        nombre
      });
      if (error) throw error;
      guard.markDirty();
      setGrupoId(nuevoId);
      setGrupoCreado({ id: nuevoId, nombre });
    } catch (err) {
      console.error(err);
      setErrMsg(err?.message || 'No se pudo crear el grupo para el torneo.');
    } finally {
      setCreandoGrupo(false);
    }
  };

  const handleSubmit = async () => {
    if (!grupoId) return;
    setEnviando(true);
    setErrMsg(null);
    try {
      // Primero guarda los números del roster (por grupo); si falla, no se envía.
      if (guardarNumerosRef.current) {
        const { error: numErr } = await guardarNumerosRef.current();
        if (numErr) throw new Error('No se pudieron guardar los números del roster.');
      }
      const { error } = await inscribirGrupo({
        torneoId: torneo.id,
        grupoId,
        // En la red viaja la division; en interno, la categoria del grupo.
        categoriaId: esLocal ? (categoriaId || null) : (categoriaSeleccionada?.categoria_id || null),
        convocatoriaId: esLocal ? null : (categoriaSeleccionada?.convocatoria_id || null)
      });
      if (error) throw error;
      guard.resetDirty();
      if (esLocal) {
        await alert('Grupo inscrito', 'Ya puedes programarle partidos en este torneo.');
      } else {
        await alert('Solicitud enviada', 'La organizadora revisará tu solicitud y te avisaremos cuando responda.');
      }
      onSuccess?.();
      onClose?.();
    } catch (err) {
      console.error(err);
      setErrMsg(mensajeErrorInscripcion(err));
    } finally {
      setEnviando(false);
    }
  };

  // En local basta el grupo: si tiene una categoria se hereda, si no tiene ninguna se
  // inscribe sin ella. Solo hay que elegir cuando el grupo tiene varias.
  const categoriaLista = esLocal
    ? catsDelGrupo !== null && (catsDelGrupo.length <= 1 || Boolean(categoriaId))
    : Boolean(categoriaId);

  const puedeEnviar = Boolean(grupoId) && categoriaLista && !enviando && !numEstado?.hayRepetidos;

  const bloqueGrupo = (
    <div className="space-y-3">
      <Label>Grupo a inscribir</Label>
      {grupoFijo ? (
        <div className="rounded-lg border bg-muted/30 p-3 text-sm">
          <p className="font-medium">{fixMojibake(grupoFijoNombre || 'Grupo')}</p>
        </div>
      ) : (
        <Tabs value={modoGrupo} onValueChange={handleModoChange}>
          {/* En un torneo local se juega con los grupos que el club ya tiene: crear
              grupos de torneo es cosa de la convocatoria de otra org. */}
          {esLocal ? null : (
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="existente">Usar grupo existente</TabsTrigger>
              <TabsTrigger value="nuevo">Crear grupo para el torneo</TabsTrigger>
            </TabsList>
          )}

          <TabsContent value="existente" className="space-y-3 pt-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar grupo..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>

            {gruposOfrecidos.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                {esLocal
                  ? 'No hay grupos disponibles. Todos los grupos que puedes inscribir ya están en este torneo.'
                  : 'No hay grupos de este género disponibles. Crea uno nuevo para este torneo.'}
              </div>
            ) : (
              <div className="space-y-2 max-h-52 overflow-auto">
                {gruposOfrecidos.map((g) => {
                  const sel = grupoId === g.id;
                  return (
                    <button
                      key={g.id}
                      type="button"
                      onClick={() => handleSelectGrupo(g.id)}
                      className={cn(
                        'w-full flex items-center gap-3 rounded-lg border p-3 text-left transition',
                        sel ? 'border-emerald-500 bg-emerald-50' : 'hover:bg-muted/50'
                      )}
                    >
                      <div className={cn(
                        'h-5 w-5 rounded-full border-2 flex items-center justify-center shrink-0',
                        sel ? 'border-emerald-500 bg-emerald-500' : 'border-gray-300'
                      )}>
                        {sel && <CheckCircle2 className="h-4 w-4 text-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {fixMojibake(g.nombre_del_grupo || 'Sin nombre')}
                        </p>
                        <div className="flex flex-wrap items-center gap-1 mt-1">
                          {g.categoria_base && (
                            <Badge variant="secondary" className="text-[10px]">
                              {fixMojibake(g.categoria_base)}
                            </Badge>
                          )}
                          {g.rama && (
                            <Badge variant="outline" className="text-[10px]">
                              {fixMojibake(g.rama)}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
            {porGenero?.sinGenero.length > 0 && (
              <p className="flex items-start gap-1 text-xs text-amber-700">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>
                  {porGenero.sinGenero.length === 1
                    ? 'Este grupo no tiene género y no se puede inscribir: '
                    : 'Estos grupos no tienen género y no se pueden inscribir: '}
                  {porGenero.sinGenero.map((g) => fixMojibake(g.nombre_del_grupo || 'Sin nombre')).join(', ')}.
                  {' '}Asígnale uno en la ficha del grupo.
                </span>
              </p>
            )}
            {porGenero?.otroGenero.length > 0 && (
              <p className="text-xs text-muted-foreground">
                {porGenero.otroGenero.length === 1
                  ? '1 grupo de otro género no aparece en esta división.'
                  : `${porGenero.otroGenero.length} grupos de otro género no aparecen en esta división.`}
              </p>
            )}
          </TabsContent>

          <TabsContent value="nuevo" className="space-y-3 pt-3">
            {grupoCreado ? (
              <div className="flex items-center gap-2 rounded-lg border border-emerald-500/40 bg-emerald-50 p-3 text-sm text-emerald-800">
                <CheckCircle2 className="h-4 w-4 shrink-0" />
                Grupo <strong className="mx-1">{fixMojibake(grupoCreado.nombre)}</strong> creado.
              </div>
            ) : (
              <>
                <div className="space-y-1">
                  <Label htmlFor="nombre-nuevo-grupo">Nombre del grupo</Label>
                  <Input
                    id="nombre-nuevo-grupo"
                    placeholder={`Ej. ${categoriaSeleccionada ? nombreConRama({ ...categoriaSeleccionada, nombre: fixMojibake(categoriaSeleccionada.nombre) }) : 'Categoría'} A`}
                    value={nombreNuevoGrupo}
                    onChange={(e) => setNombreNuevoGrupo(e.target.value)}
                  />
                </div>
                <p className="text-xs text-muted-foreground flex items-start gap-1">
                  <Sparkles className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  Se creará con las reglas de edad y el género de la división, y no cuenta contra
                  el límite de grupos de tu plan.
                </p>
                <Button
                  variant="outline"
                  onClick={handleCrearGrupo}
                  disabled={!nombreNuevoGrupo.trim() || creandoGrupo}
                >
                  {creandoGrupo ? 'Creando grupo...' : 'Crear grupo'}
                </Button>
              </>
            )}
          </TabsContent>
        </Tabs>
      )}

      {grupoId && categoriaSeleccionada && (
        <div className="rounded-lg border bg-muted/30 p-3 text-sm">
          {loadingJugadores ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Revisando elegibilidad de jugadores...
            </div>
          ) : elegibilidad ? (
            <div className="space-y-2">
              <p className="font-medium">
                {elegibilidad.elegibles} de {elegibilidad.total} jugador(es) elegibles para{' '}
                {esLocal ? fixMojibake(categoriaSeleccionada?.nombre || '') : etiquetaLimpia(categoriaSeleccionada)}
              </p>
              {elegibilidad.elegibles < elegibilidad.total && (
                <div className="flex items-start gap-2 text-amber-700">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                  <p className="text-xs">
                    {esLocal
                      ? 'Algunos jugadores quedan fuera del rango de edad de la categoría. Puedes inscribir el grupo de todos modos.'
                      : 'Algunos jugadores no cumplen el rango de edad de la categoría. La organizadora revisará el roster; puedes inscribir el grupo de todos modos.'}
                  </p>
                </div>
              )}
              {elegibilidad.total > 0 && (
                <ul className="text-xs text-muted-foreground space-y-0.5 max-h-24 overflow-auto">
                  {elegibilidad.jugadores.filter((j) => !j.elegible).map((j) => (
                    <li key={j.id}>
                      {fixMojibake(j.nombre_completo || 'Jugador')} — {j.motivo}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );

  // Externo: la organizadora dice en que categorias se juega.
  const bloqueCategoriaConvocada = (
    <div className="space-y-2">
      <Label>División convocada</Label>
      {convocadas.length === 0 ? (
        <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
          Este torneo aún no tiene categorías convocadas. Pídele a la organizadora que
          agregue categorías antes de inscribir grupos.
        </div>
      ) : grupoFijoSinGenero ? (
        <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            Este grupo no tiene género y no se puede inscribir en este torneo. Asígnale uno en la
            ficha del grupo.
          </p>
        </div>
      ) : divisionesOfrecidas.length === 0 ? (
        <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>Este torneo no convoca divisiones {ramaFijaNormalizada} para esta categoría.</p>
        </div>
      ) : (
        <Select value={categoriaId} onValueChange={handleCategoriaChange}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Selecciona una división" />
          </SelectTrigger>
          <SelectContent>
            {divisionesOfrecidas.map((c) => (
              <SelectItem key={idDivision(c)} value={idDivision(c)}>
                {etiquetaLimpia(c)} · {formatRangoEdad(c)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );

  // Local: la categoria sale del grupo. Casi siempre no hay nada que preguntar.
  const bloqueCategoriaDelGrupo = (
    <div className="space-y-2">
      <Label>Categoría</Label>
      {!grupoId ? (
        <p className="text-sm text-muted-foreground">Elige primero el grupo.</p>
      ) : catsDelGrupo === null ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Buscando la categoría del grupo...
        </p>
      ) : catsDelGrupo.length === 0 ? (
        <div className="rounded-lg border border-dashed p-3 text-sm text-muted-foreground">
          Este grupo no tiene categoría asignada. Se inscribe sin categoría; puedes
          asignársela después desde el grupo.
        </div>
      ) : catsDelGrupo.length === 1 ? (
        <div className="rounded-lg border bg-muted/30 p-3 text-sm">
          <span className="font-medium">{fixMojibake(catsDelGrupo[0].nombre)}</span>
          <span className="text-muted-foreground"> · {formatRangoEdad(catsDelGrupo[0])}</span>
          <p className="mt-1 text-xs text-muted-foreground">Heredada del grupo.</p>
        </div>
      ) : (
        <>
          <Select value={categoriaId} onValueChange={handleCategoriaChange}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Selecciona una categoría" />
            </SelectTrigger>
            <SelectContent>
              {catsDelGrupo.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {fixMojibake(c.nombre)} · {formatRangoEdad(c)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Este grupo juega en varias categorías: elige con cuál lo inscribes.
          </p>
        </>
      )}
    </div>
  );

  // Era un portal a mano (fondo y tarjeta propios) y no heredaba la pantalla completa en
  // telefono, donde mas se usa: el coach inscribe su grupo desde la cancha.
  return (
    <>
    <Dialog open onOpenChange={(open) => { if (!open) guard.onOpenChange(false); }}>
      <DialogContent {...guard.dirtyProps} className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Inscribir grupo</DialogTitle>
          <DialogDescription>
            {esLocal === false ? 'Solicita la inscripción de un grupo de tu club en ' : 'Inscribe un grupo de tu club en '}
            <strong>{fixMojibake(torneo?.nombre || '')}</strong>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {cargando ? (
            <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Cargando...
            </div>
          ) : esLocal ? (
            <>
              {bloqueGrupo}
              <div className="border-t pt-4">{bloqueCategoriaDelGrupo}</div>
            </>
          ) : (
            <>
              {bloqueCategoriaConvocada}
              {categoriaId && <div className="border-t pt-4">{bloqueGrupo}</div>}
            </>
          )}

          {/* En interno el unico tope posible es el del plan del club, y hay que decirlo
              asi: "en este torneo" sonaba a regla del torneo, que no existe. */}
          {!cargando && maxJugadores != null && (
            <p className="text-xs text-muted-foreground">
              {esLocal
                ? `Tu plan permite hasta ${maxJugadores} jugadores por roster.`
                : `Máx. ${maxJugadores} jugadores por grupo en este torneo.`}
            </p>
          )}

          {grupoId && categoriaLista && (
            <div className="space-y-2 border-t pt-4">
              <Label>Números del roster</Label>
              <p className="text-xs text-muted-foreground">
                Cada jugador con su número (único en el grupo). Es el número que se usará en el torneo;
                se prellena con su número de club.
              </p>
              <NumerosGrupoEditor
                grupoId={grupoId}
                onEstadoChange={setNumEstado}
                saveRef={guardarNumerosRef}
              />
            </div>
          )}
        </div>

        <div className="border-t pt-4 space-y-3">
          {errMsg && (
            <Alert variant="destructive">
              <AlertDescription>{errMsg}</AlertDescription>
            </Alert>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => guard.onOpenChange(false)} disabled={enviando}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit} disabled={!puedeEnviar}>
              {/* Mientras no se sabe el mundo (null) no se promete "solicitud". */}
              {enviando
                ? (esLocal === false ? 'Enviando...' : 'Inscribiendo...')
                : (esLocal === false ? 'Enviar solicitud' : 'Inscribir grupo')}
            </Button>
          </div>
        </div>
        {guard.DiscardDialog}
      </DialogContent>
    </Dialog>
    <ConfirmDialog />
    </>
  );
};

export default InscribirGrupoModal;
